import { useMediaQuery } from "@mui/system";
import permissions from "app/permissions";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import {
  BooleanInput,
  Datagrid,
  DateField,
  FunctionField,
  List,
  NumberInput,
  SelectInput,
  ShowButton,
  SimpleList,
  TextField,
  TextInput,
  TopToolbar,
} from "react-admin";
import SecuredComponent from "server/security/SecuredComponent";
import StatusChip from "server/components/StatusChip";
import {
  NacidFlowStatusChoices,
  NacidFlowStatusColorMap,
  NacidFlowTypeChoices,
} from "utils/choices";
import { MOBILE_BREAKPOINT } from "utils/constants";
import ReconcileCoverageButton from "./ReconcileCoverageButton";

const typeLabel = record => NacidFlowTypeChoices.find(x => x.id === record?.type)?.name ?? record?.type;

// Sources are the backend's own query-parameter names — the data provider appends `&<source>=<value>` verbatim.
const listFilters = [
  <SelectInput
    source="Status"
    label="Статус"
    choices={NacidFlowStatusChoices}
    emptyText="Всички"
    alwaysOn
  />,
  <SelectInput
    source="Type"
    label="Тип"
    choices={NacidFlowTypeChoices}
    emptyText="Всички"
    alwaysOn
  />,
  <NumberInput
    source="StudentId"
    label="Студент №"
    alwaysOn
  />,
  <TextInput
    source="Uan"
    label="ЕАН"
    alwaysOn
  />,
  // The operator's first question is "what needs me right now?" — this is the answer to it.
  <BooleanInput
    source="OnlyActionable"
    label="Само за намеса"
    alwaysOn
  />,
];

const ListActions = () => (
  <TopToolbar>
    <SecuredComponent permission={permissions.canManageNacidFlows}>
      <ReconcileCoverageButton />
    </SecuredComponent>
  </TopToolbar>
);

const flowStatusChip = record => (
  <StatusChip
    status={record?.status}
    choices={NacidFlowStatusChoices}
    colorMap={NacidFlowStatusColorMap}
  />
);

const flowProgress = record => `${record?.currentStepOrder ?? 0} / ${record?.stepsCount ?? 0}`;

const NacidFlowList = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  return (
    <List
      title="НАЦИД потоци"
      exporter={false}
      filters={listFilters}
      actions={<ListActions />}
    >
      {isMobile ? (
        <SimpleList
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "Тип", value: typeLabel(record) }]} />
              <MobileListRow fields={[{ label: "Статус", value: flowStatusChip(record) }]} />
            </div>
          )}
          secondaryText={record => (
            <div>
              <MobileListRow
                fields={[
                  { label: "Студент", value: record?.studentId },
                  { label: "ЕАН", value: record?.uan || "—" },
                ]}
              />
              <MobileListRow fields={[{ label: "Стъпка", value: flowProgress(record) }]} />
              <MobileListRow fields={[{ label: "Причина", value: record?.manualReasonCode || "—" }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Създаден",
                    value: (
                      <DateField
                        source="createdOn"
                        locales="bg"
                        showTime
                      />
                    ),
                  },
                ]}
              />
            </div>
          )}
          tertiaryText={() => <ShowButton label="" />}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick="show"
          sx={{ "& .RaDatagrid-headerCell": { textAlign: "left" } }}
        >
          {/* Sorting is off on every column: the backend orders by id descending and ignores SortBy/SortOrder. */}
          <FunctionField
            label="Статус"
            render={flowStatusChip}
            sortable={false}
          />
          <FunctionField
            label="Тип"
            render={typeLabel}
            sortable={false}
          />
          <TextField
            source="studentId"
            label="Студент №"
            sortable={false}
          />
          <TextField
            source="uan"
            label="ЕАН"
            emptyText="—"
            sortable={false}
          />
          <FunctionField
            label="Стъпка"
            render={flowProgress}
            sortable={false}
          />
          <TextField
            source="manualReasonCode"
            label="Причина за намеса"
            emptyText="—"
            sortable={false}
          />
          <DateField
            source="createdOn"
            label="Създаден"
            locales="bg"
            showTime
            sortable={false}
          />
          <ShowButton />
        </Datagrid>
      )}
    </List>
  );
};

export default NacidFlowList;
